import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SubmitButton from '../../../components/SubmitButton';
import Label from '../../../components/Label';
import { axiosKK } from '../../../services/networkRequest';
import {
  AdminOrderRow,
  AdminOrdersResponse,
  AdminOrderAssignmentResponse,
  AvailableCouriersResponse,
  WarehousesResponse,
  OrderAssignmentResponse,
  PartnerUser,
  Warehouse,
  LogisticsDriverOption,
  LogisticsDriversResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import ReactDOM from 'react-dom';
import { Formik } from 'formik';
import IssueModal from '../../../components/admin/IssueModal';
import SplitOrderModal from '../../../components/admin/SplitOrderModal';
import { downloadPdf } from '../../../services/downloadBlob';
import {
  FilterBar,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  CloseButton,
  AdminNotice,
  EmptyState,
  SectionHead,
} from '../../../components/admin/Shared';
import {
  DateRangeDropdown,
  backwardPresets,
} from '../../../components/DateDropdown';
import {
  LockClosedIcon,
  UserPlusIcon,
  FlagIcon,
  RectangleGroupIcon,
  PrinterIcon,
} from '@heroicons/react/24/solid';
import { legOf, withAssignment, withLegDriver } from './orderRows';
import {
  AssignmentFormValues,
  buildAssignmentPayload,
  courierPartnerChoices,
  couriersAfterPartnerChange,
  couriersFor,
  initialReturnPartnerId,
  returnLegPartnerId,
  idOf,
  warehouseGroups,
} from './assignment';
import { AdminPartner } from '../../../components/admin/CreatePartnerModal';
import { fetchAdminPartners } from '../../../services/adminPartners';
import { isLinked, syncStatusText } from '../../../services/hubspotSync';

const PAGE_SIZE = 15;

/** What the per-leg driver endpoints return (storage-api CollectionRow / ReturnRow). */
interface LegDriverRow {
  id: number;
  driver: string | null;
  driverId?: number | null;
}

function getCourierDisplayName(courier: PartnerUser): string {
  if (courier.firstName || courier.lastName) {
    return [courier.firstName, courier.lastName].filter(Boolean).join(' ');
  }
  return courier.name || courier.email || `Courier #${courier.id}`;
}

function getCourierPhone(courier: PartnerUser): string | undefined {
  return courier.phoneNumber || courier.phone;
}

function formatTimeslot(timeslot: string | null): string {
  if (!timeslot) return '—';
  const lower = timeslot.toLowerCase();
  if (lower.includes('morning') || lower.includes('am')) return 'Morning';
  if (lower.includes('afternoon') || lower.includes('pm')) return 'Afternoon';
  const match = timeslot.match(/(\d{1,2}):?\d{0,2}\s*[-–]\s*(\d{1,2})/);
  if (match) {
    const endHour = parseInt(match[2], 10);
    return endHour <= 12 ? 'Morning' : 'Afternoon';
  }
  return timeslot;
}

const ETA_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    ETA_OPTIONS.push(
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    );
  }
}

interface OrdersTabProps {
  partnerId: number | null;
  /** List the orders no partner has yet, instead of one partner's. */
  unassigned?: boolean;
  /** List every order, assigned or not. */
  allOrders?: boolean;
}

export default function OrdersTab({ partnerId, unassigned = false, allOrders = false }: OrdersTabProps) {
  const hasScope = partnerId != null || unassigned || allOrders;
  // With no partner and no unassigned flag the endpoint answers with every order.
  const unassignedOnly = partnerId == null && unassigned && !allOrders;
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [networkError, setNetworkError] = useState<string>();
  const [fetching, setFetching] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [selectedOrder, setSelectedOrder] = useState<AdminOrderRow | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [issueOrder, setIssueOrder] = useState<AdminOrderRow | null>(null);
  const [splitOrder, setSplitOrder] = useState<AdminOrderRow | null>(null);

  const [drivers, setDrivers] = useState<LogisticsDriverOption[]>([]);
  useEffect(() => {
    if (partnerId == null) {
      setDrivers([]);
      return;
    }
    axiosKK
      .get<LogisticsDriversResponse>('/logistics/dashboard/partner/drivers', {
        params: { partnerId },
      })
      .then(({ data }) => setDrivers(data.drivers || []))
      .catch(() => {
        // Silent — inline picker just becomes unavailable.
      });
  }, [partnerId]);

  // The endpoints answer with a partner-dashboard CollectionRow / ReturnRow, not
  // an AdminOrderRow: its `driverId` / `driver` are this leg's courier and it has
  // no `type`. Treating it as an AdminOrderRow matched no row, so the controlled
  // select fell back to "Not assigned" although the change had been saved.
  // The pick is shown at once and put back if the save is refused.
  const updateDriver = (order: AdminOrderRow, newDriverId: number | null) => {
    const leg = legOf(order);
    const endpoint =
      leg === 'delivery'
        ? `/logistics/dashboard/partner/returns/${order.id}/driver`
        : `/logistics/dashboard/partner/collections/${order.id}/driver`;
    const previousId = leg === 'delivery' ? order.deliveryDriverId : order.collectionDriverId;
    const previousName = leg === 'delivery' ? order.deliveryDriverName : order.collectionDriverName;
    const pickedName = drivers.find((d) => d.id === newDriverId)?.name ?? null;

    setNetworkError(undefined);
    setOrders((prev) => withLegDriver(prev, order.id, leg, newDriverId, pickedName));
    axiosKK
      .put<LegDriverRow>(endpoint, { driverId: newDriverId }, {
        params: partnerId != null ? { partnerId } : undefined,
      })
      .then(({ data }) =>
        setOrders((prev) =>
          withLegDriver(prev, order.id, leg, data.driverId ?? null, data.driver ?? null)
        )
      )
      .catch((err) => {
        setOrders((prev) => withLegDriver(prev, order.id, leg, previousId, previousName));
        errorMessage(setNetworkError)(err);
      });
  };

  const driverPicker = (o: AdminOrderRow, value: number | null, name: string | null) => (
    <select
      aria-label={`${legOf(o) === 'delivery' ? 'Return' : 'Collection'} courier for order ${o.id}`}
      value={value ?? ''}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) =>
        updateDriver(o, e.target.value ? Number(e.target.value) : null)
      }
    >
      <option value="">— Not assigned —</option>
      {value != null && !drivers.some((d) => d.id === value) && (
        // Assigned to someone the picker no longer lists (deactivated, or
        // another partner's). Without this option the select would show
        // "Not assigned" for an order that is assigned.
        <option value={value}>{name || `Courier #${value}`}</option>
      )}
      {drivers.map((d) => (
        <option key={d.id} value={d.id}>
          {d.name}
        </option>
      ))}
    </select>
  );

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    if (!hasScope) {
      setOrders([]);
      setTotalCount(0);
      setFetching(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    setNetworkError(undefined);

    const params: any = { page, pageSize: PAGE_SIZE };
    if (partnerId != null) params.partnerId = partnerId;
    else if (unassignedOnly) params.unassigned = true;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (typeFilter) params.type = typeFilter;

    axiosKK
      .get<AdminOrdersResponse>('/logistics/admin/orders', { params })
      .then(({ data }) => {
        if (cancelled) return;
        setOrders(data.orders);
        setTotalCount(data.totalCount);
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => { cancelled = true; };
  }, [page, fromDate, toDate, partnerId, unassignedOnly, hasScope, typeFilter]);

  const handleFilterChange = (setter: (v: string) => void) => (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleUpdateOrder = (updated: AdminOrderRow) => {
    setOrders((prev) => withAssignment(prev, updated));
  };

  const orderColumns: DataTableColumn<AdminOrderRow>[] = [
    {
      key: 'id',
      label: 'ID',
      sticky: true,
      render: (o) => <span className="logistics-order-id">#{o.id}</span>,
    },
    {
      key: 'type',
      label: 'Type',
      render: (o) => (
        <span className={`logistics-type-badge logistics-type-badge--${o.type}`}>
          {o.type}
        </span>
      ),
    },
    { key: 'date', label: 'Date', render: (o) => o.date || '—' },
    { key: 'customer', label: 'Customer', render: (o) => o.customer || '—' },
    {
      key: 'address',
      label: 'Address',
      render: (o) => `${o.address || '—'}, ${o.postcode || ''}`.replace(/, $/, ''),
    },
    { key: 'boxes', label: 'Boxes', render: (o) => o.boxes ?? '—' },
    { key: 'partnerName', label: 'Partner', render: (o) => o.partnerName || '—' },
    // Each row edits its own leg. The picker used to sit in this column on every
    // row while a return row's save went to the delivery leg, so choosing a
    // "collection courier" on a return set the delivery courier instead.
    {
      key: 'collectionCourier',
      label: 'Collection Courier',
      render: (o) =>
        legOf(o) === 'collection' && drivers.length > 0
          ? driverPicker(o, o.collectionDriverId, o.collectionDriverName)
          : o.collectionDriverName || '—',
    },
    {
      key: 'deliveryCourier',
      label: 'Return Courier',
      render: (o) =>
        legOf(o) === 'delivery' && drivers.length > 0
          ? driverPicker(o, o.deliveryDriverId, o.deliveryDriverName)
          : o.deliveryDriverName || '—',
    },
    { key: 'warehouseName', label: 'Warehouse', render: (o) => o.warehouseName || '—' },
    { key: 'collectionEta', label: 'Collection ETA', render: (o) => o.collectionEta || '—' },
    { key: 'deliveryEta', label: 'Delivery ETA', render: (o) => o.deliveryEta || '—' },
    {
      key: 'timeslot',
      label: 'Requested Timeslot',
      render: (o) => formatTimeslot(o.timeslot),
    },
    {
      key: 'status',
      label: 'Status',
      render: (o) => {
        const items: React.ReactNode[] = [];
        if (o.routeLocked)
          items.push(
            <span key="locked" className="logistics-badge logistics-badge--locked">
              <LockClosedIcon width={14} height={14} /> Locked
            </span>
          );
        if (o.doorstep)
          items.push(
            <span key="door" className="logistics-badge logistics-badge--doorstep">
              Doorstep
            </span>
          );
        if (o.lastScan)
          items.push(
            <span key="scan" className="logistics-badge logistics-badge--scan">
              {o.lastScan}
            </span>
          );
        return items.length > 0 ? <>{items}</> : '—';
      },
    },
  ];

  return (
    <>
      <SectionHead
        title={unassignedOnly ? 'Unassigned orders' : allOrders && partnerId == null ? 'All orders' : 'Active orders'}
        caption={
          unassignedOnly
            ? 'Storage orders that no partner has yet. Use Assign to give one a partner, couriers and a warehouse.'
            : allOrders && partnerId == null
              ? 'Every upcoming storage order, whichever partner has it, with its collection and return scheduling.'
              : 'Storage orders for the selected partner with their collection and return scheduling. Filter by status, date or driver assignment.'
        }
        infoKey="logistics.orders"
      />
      <AdminNotice intent="error">{networkError}</AdminNotice>

      <section className="admin-logistics-orders">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint="/logistics/admin/orders"
              filename={`logistics-orders-${fromDate || 'all'}.csv`}
              params={{
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
                partnerId: partnerId ?? undefined,
                unassigned: unassignedOnly ? 'true' : undefined,
                type: typeFilter || undefined,
              }}
            />
          }
        >
          <FilterBar.Item label="Date range" htmlFor="orders-date-range">
            <DateRangeDropdown
              id="orders-date-range"
              value={{ start: fromDate || null, end: toDate || null }}
              onChange={({ start, end }) => {
                setPage(1);
                setFromDate(start ?? '');
                setToDate(end ?? '');
              }}
              label="Date range"
              placeholder="All dates"
              presets={backwardPresets()}
            />
          </FilterBar.Item>
          <FilterBar.Item label="Type" htmlFor="orders-type">
            <select
              id="orders-type"
              value={typeFilter}
              onChange={handleFilterChange(setTypeFilter)}
            >
              <option value="">All</option>
              <option value="collection">Collection</option>
              <option value="return">Return</option>
            </select>
          </FilterBar.Item>
        </FilterBar>

        {!hasScope ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Pick a partner from the picker above to load orders."
          />
        ) : (
          <DataTable<AdminOrderRow>
            columns={orderColumns}
            rows={orders}
            keyOf={(o) => `${o.id}-${o.type}`}
            loading={fetching}
            emptyMessage="No orders found"
            loadingMessage="Loading orders…"
            actionsColumn={{
              label: 'Actions',
              render: (order) => (
                <button
                  className="logistics-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(order);
                    setShowAssignModal(true);
                  }}
                >
                  <UserPlusIcon width={14} height={14} /> Assign
                </button>
              ),
            }}
          />
        )}

        {hasScope && (
        <div className="logistics-pagination">
          <button
            className="logistics-pagination__btn"
            disabled={page <= 1 || fetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            &#8249;
          </button>
          <span className="logistics-pagination__info">
            {fetching && orders.length > 0
              ? 'Loading...'
              : `Page ${page} of ${totalPages || 1}`}
          </span>
          <button
            className="logistics-pagination__btn"
            disabled={page >= totalPages || fetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            &#8250;
          </button>
        </div>
        )}
      </section>

      {issueOrder && (
        <IssueModal
          show={true}
          orderId={issueOrder.id}
          close={() => setIssueOrder(null)}
        />
      )}

      {splitOrder && (
        <SplitOrderModal
          show={true}
          orderId={splitOrder.id}
          partnerId={splitOrder.partnerId ?? partnerId}
          close={() => setSplitOrder(null)}
        />
      )}

      {selectedOrder && (
        <AssignmentModal
          show={showAssignModal}
          order={selectedOrder}
          close={() => {
            setShowAssignModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handleUpdateOrder}
          onFlag={() => {
            setIssueOrder(selectedOrder);
            setShowAssignModal(false);
          }}
          onSplit={() => {
            setSplitOrder(selectedOrder);
            setShowAssignModal(false);
          }}
          onDownloadLabels={() =>
            downloadPdf(
              `/logistics/orders/${selectedOrder.id}/labels`,
              `labels-order-${selectedOrder.id}.pdf`
            ).catch((err) => errorMessage(setNetworkError)(err))
          }
        />
      )}
    </>
  );
}

interface AssignmentModalProps {
  show: boolean;
  order: AdminOrderRow;
  close: () => void;
  onSuccess: (updated: AdminOrderRow) => void;
  onFlag: () => void;
  onSplit: () => void;
  onDownloadLabels: () => void;
}

function AssignmentModal({
  show,
  order,
  close,
  onSuccess,
  onFlag,
  onSplit,
  onDownloadLabels,
}: AssignmentModalProps) {
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [couriers, setCouriers] = useState<PartnerUser[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [networkError, setNetworkError] = useState<string>();
  const [loadingData, setLoadingData] = useState(false);
  const [initialValues, setInitialValues] = useState<AssignmentFormValues>({
    collectionCourierId: '',
    deliveryCourierId: '',
    partnerId: order.partnerId || '',
    returnPartnerId: initialReturnPartnerId(order),
    warehouseId: '',
    collectionEta: order.collectionEta || '',
    deliveryEta: order.deliveryEta || '',
    collectionEtaStart: order.collectionEtaStart || '',
    collectionEtaEnd: order.collectionEtaEnd || '',
    deliveryEtaStart: order.deliveryEtaStart || '',
    deliveryEtaEnd: order.deliveryEtaEnd || '',
  });
  // What the order has now, for the options that must show it even when it
  // would not be offered today (a deactivated courier, another partner's driver
  // left over from before the API checked).
  const [current, setCurrent] = useState<{
    collectionCourier?: PartnerUser;
    deliveryCourier?: PartnerUser;
    warehouse?: Warehouse;
  }>({});

  // Read out here so the effect depends on the answer, not on the whole order
  // object (a new one arrives with every list refresh).
  const initialReturnPartner = initialReturnPartnerId(order);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingData(true);
    setNetworkError(undefined);

    Promise.all([
      fetchAdminPartners(),
      axiosKK.get<AvailableCouriersResponse>('/admin/logistics/couriers'),
      axiosKK.get<WarehousesResponse>('/admin/logistics/warehouses'),
      axiosKK.get<OrderAssignmentResponse>(
        `/admin/logistics/orders/${order.id}/assignment`
      ),
    ])
      .then(([partnersList, couriersRes, warehousesRes, assignmentRes]) => {
        if (cancelled) return;
        setPartners(partnersList);
        setCouriers(couriersRes.data.couriers || []);
        setWarehouses(warehousesRes.data.warehouses || []);

        const assignment = assignmentRes.data;
        const collectionCourier = assignment.collectionCourier ?? assignment.courier;
        const deliveryCourier = assignment.deliveryCourier ?? assignment.courier;
        setCurrent({
          collectionCourier: collectionCourier ?? undefined,
          deliveryCourier: deliveryCourier ?? undefined,
          warehouse: assignment.warehouse ?? undefined,
        });

        setInitialValues({
          collectionCourierId: collectionCourier?.id || '',
          deliveryCourierId: deliveryCourier?.id || '',
          partnerId: order.partnerId || '',
          returnPartnerId: initialReturnPartner,
          warehouseId: assignment.warehouse?.id || '',
          collectionEta: order.collectionEta || '',
          deliveryEta: order.deliveryEta || '',
          collectionEtaStart: order.collectionEtaStart || '',
          collectionEtaEnd: order.collectionEtaEnd || '',
          deliveryEtaStart: order.deliveryEtaStart || '',
          deliveryEtaEnd: order.deliveryEtaEnd || '',
        });
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [
    show,
    order.id,
    order.partnerId,
    order.collectionEta,
    order.deliveryEta,
    order.collectionEtaStart,
    order.collectionEtaEnd,
    order.deliveryEtaStart,
    order.deliveryEtaEnd,
    initialReturnPartner,
  ]);

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  const partnerOptions = React.useMemo(
    () => courierPartnerChoices(partners, order.partnerId ?? null, order.partnerName),
    [partners, order.partnerId, order.partnerName]
  );

  const groups = React.useMemo(() => {
    const list = [...warehouses];
    const assigned = current.warehouse;
    if (assigned && !list.some((w) => w.id === assigned.id)) list.push(assigned);
    return warehouseGroups(list, partners);
  }, [warehouses, partners, current.warehouse]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container logistics-modal__container--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            setNetworkError(undefined);

            const payload = buildAssignmentPayload(initialValues, values);
            if (Object.keys(payload).length === 0) {
              setSubmitting(false);
              close();
              return;
            }

            axiosKK
              .put<AdminOrderAssignmentResponse>(
                `/logistics/admin/orders/${order.id}/assignment`,
                payload
              )
              .then(({ data }) => {
                onSuccess(data.order);
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({ values, isSubmitting, handleChange, handleSubmit, isValid, dirty, setValues }) => {
            const selectedPartnerId = idOf(values.partnerId);
            const selectedPartner = partners.find((p) => p.id === selectedPartnerId);
            const partnerCouriers = couriersFor(couriers, selectedPartnerId);
            const returnLegId = returnLegPartnerId(values, initialValues, order);
            const returnLegPartner = partners.find((p) => p.id === returnLegId);
            const returnCouriers = couriersFor(couriers, returnLegId);
            const linkedPartnerNames = partners.map((p) => p.hubspotName);
            const courierOptions = (currentCourier: PartnerUser | undefined, legPartnerId: number | null) => {
              const list = [...couriersFor(couriers, legPartnerId)];
              // The courier the order has now, if the list would not otherwise
              // show them — the select must not claim "Not assigned" when it is.
              if (currentCourier && !list.some((c) => c.id === currentCourier.id)) {
                list.push(currentCourier);
              }
              return list.map((courier) => {
                const displayName = getCourierDisplayName(courier);
                const phone = getCourierPhone(courier);
                const elsewhere = courier.partnerId !== legPartnerId;
                return (
                  <option key={courier.id} value={courier.id}>
                    {displayName}
                    {phone ? ` (${phone})` : ''}
                    {elsewhere ? ' — not with this partner' : ''}
                  </option>
                );
              });
            };
            const onPartnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
              const next = { ...values, partnerId: e.target.value };
              setValues({
                ...next,
                ...couriersAfterPartnerChange(
                  values,
                  couriers,
                  idOf(next.partnerId),
                  returnLegPartnerId(next, initialValues, order)
                ),
              });
            };
            const onReturnPartnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
              const next = { ...values, returnPartnerId: e.target.value };
              setValues({
                ...next,
                deliveryCourierId: couriersAfterPartnerChange(
                  values,
                  couriers,
                  idOf(next.partnerId),
                  returnLegPartnerId(next, initialValues, order)
                ).deliveryCourierId,
              });
            };
            const syncText = syncStatusText(order.hubspotSync, order.hubspotSyncError);
            const hubspotHint = (raw: string | null | undefined, linked: boolean, what: string) =>
              raw ? (
                <p className="logistics-modal__hint">
                  HubSpot says {raw}
                  {linked ? '.' : `, which is not linked to any ${what} here.`}
                </p>
              ) : null;
            return (
            <form
              onSubmit={handleSubmit}
              className="logistics-modal__form"
              name="logistics-assign-form"
            >
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Assign Order</h3>
                  <span className="logistics-modal__subtitle">
                    Order #{order.id} &middot; {order.customer}
                  </span>
                </div>
                <CloseButton onClick={close} ariaLabel="Close assignment modal" />
              </div>

              <div className="logistics-modal__secondary-actions">
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onFlag}
                >
                  <FlagIcon width={14} height={14} /> Flag
                </button>
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onSplit}
                >
                  <RectangleGroupIcon width={14} height={14} /> Split
                </button>
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onDownloadLabels}
                >
                  <PrinterIcon width={14} height={14} /> Labels
                </button>
              </div>

              <div className="logistics-modal__body">
                {syncText && (
                  <p
                    className="logistics-modal__hint"
                    role="status"
                    data-testid="hubspot-sync-status"
                  >
                    {syncText}
                  </p>
                )}
                {loadingData ? (
                  <div className="logistics-modal__loading">
                    Loading assignment data...
                  </div>
                ) : (
                  <>
                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Partner</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="partnerId">Courier partner</Label>
                        <select
                          className="logistics-modal__select"
                          id="partnerId"
                          name="partnerId"
                          value={values.partnerId}
                          onChange={onPartnerChange}
                        >
                          <option value="">— Not assigned —</option>
                          {partnerOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.current ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                        {partnerOptions.length === 0 && (
                          <p className="logistics-modal__hint">
                            No courier partners yet.{' '}
                            <Link to="/admin/partners">Add one under Partners</Link>.
                          </p>
                        )}
                        {hubspotHint(order.hubspotCourier, isLinked(order.hubspotCourier, linkedPartnerNames), 'partner')}
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="returnPartnerId">Return partner</Label>
                        <select
                          className="logistics-modal__select"
                          id="returnPartnerId"
                          name="returnPartnerId"
                          value={values.returnPartnerId ?? ''}
                          onChange={onReturnPartnerChange}
                        >
                          <option value="">— Same as the collection partner —</option>
                          {partnerOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.current ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                        {hubspotHint(
                          order.hubspotReturnCourier,
                          isLinked(order.hubspotReturnCourier, linkedPartnerNames),
                          'partner'
                        )}
                        {returnLegId == null && order.hubspotReturnCourier && idOf(values.returnPartnerId) == null && (
                          <p className="logistics-modal__hint">
                            Nobody here holds the return until a partner is chosen.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Couriers</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionCourierId">Collection Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionCourierId"
                          name="collectionCourierId"
                          value={values.collectionCourierId}
                          onChange={handleChange}
                          disabled={selectedPartnerId == null}
                        >
                          <option value="">
                            {selectedPartnerId == null ? '— Choose a partner first —' : '— Not assigned —'}
                          </option>
                          {courierOptions(current.collectionCourier, selectedPartnerId)}
                        </select>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryCourierId">Return Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryCourierId"
                          name="deliveryCourierId"
                          value={values.deliveryCourierId}
                          onChange={handleChange}
                          disabled={returnLegId == null}
                        >
                          <option value="">
                            {returnLegId == null ? '— Choose a partner first —' : '— Not assigned —'}
                          </option>
                          {courierOptions(current.deliveryCourier, returnLegId)}
                        </select>
                      </div>
                      {selectedPartnerId != null && partnerCouriers.length === 0 && (
                        <p className="logistics-modal__hint">
                          {selectedPartner?.name || 'This partner'} has no active drivers yet.{' '}
                          <Link to="/admin/partner-access">Add one under Partners → User access</Link>.
                        </p>
                      )}
                      {returnLegId != null && returnLegId !== selectedPartnerId && returnCouriers.length === 0 && (
                        <p className="logistics-modal__hint">
                          {returnLegPartner?.name || 'The return partner'} has no active drivers yet.{' '}
                          <Link to="/admin/partner-access">Add one under Partners → User access</Link>.
                        </p>
                      )}
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Warehouse</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="warehouseId">Assign Warehouse</Label>
                        <select
                          className="logistics-modal__select"
                          id="warehouseId"
                          name="warehouseId"
                          value={values.warehouseId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {groups.map((g) => (
                            <optgroup key={g.partnerId} label={g.partnerName}>
                              {g.warehouses.map((wh) => {
                                const location = [wh.city, wh.postcode].filter(Boolean).join(', ');
                                return (
                                  <option key={wh.id} value={wh.id}>
                                    {wh.name}{location ? ` - ${location}` : ''}
                                    {wh.isActive === false ? ' (switched off)' : ''}
                                  </option>
                                );
                              })}
                            </optgroup>
                          ))}
                        </select>
                        {hubspotHint(
                          order.hubspotWarehouse,
                          isLinked(order.hubspotWarehouse, warehouses.map((w) => w.hubspotName)),
                          'warehouse location'
                        )}
                        {warehouses.length === 0 && (
                          <p className="logistics-modal__hint">
                            No warehouse locations yet. Open a warehouse partner under{' '}
                            <Link to="/admin/partners">Partners</Link> and add one in its
                            Warehouses section.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">ETAs</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionEta">Collection ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionEta"
                          name="collectionEta"
                          value={values.collectionEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="logistics-modal__field logistics-modal__field--inline">
                        <Label>Collection window (override)</Label>
                        <div className="eta-window-row">
                          <select
                            className="logistics-modal__select"
                            name="collectionEtaStart"
                            value={values.collectionEtaStart}
                            onChange={handleChange}
                          >
                            <option value="">— start —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="eta-window-row__sep">→</span>
                          <select
                            className="logistics-modal__select"
                            name="collectionEtaEnd"
                            value={values.collectionEtaEnd}
                            onChange={handleChange}
                          >
                            <option value="">— end —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryEta">Delivery ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryEta"
                          name="deliveryEta"
                          value={values.deliveryEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="logistics-modal__field logistics-modal__field--inline">
                        <Label>Delivery window (override)</Label>
                        <div className="eta-window-row">
                          <select
                            className="logistics-modal__select"
                            name="deliveryEtaStart"
                            value={values.deliveryEtaStart}
                            onChange={handleChange}
                          >
                            <option value="">— start —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="eta-window-row__sep">→</span>
                          <select
                            className="logistics-modal__select"
                            name="deliveryEtaEnd"
                            value={values.deliveryEtaEnd}
                            onChange={handleChange}
                          >
                            <option value="">— end —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={loadingData || !isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Save Assignment
                </SubmitButton>
              </div>
            </form>
            );
          }}
        </Formik>
      </div>
    </div>,
    document.body
  );
}
